/**
 * @jsxRuntime classic
 * @jsx jsx
 */
import React, { useCallback } from 'react';

import { jsx } from '@compiled/react';

import { FlagsProvider } from '@atlaskit/flag';
import { BFFConsumerEnum, BFFProvider } from '@atlassian/navigation-bff-data-provider';
import { ConditionalRelayEnvironmentProvider } from '@atlassian/relay-environment-provider';

import type { UniversalCreateButtonCombinedProps } from '../../../types';
import AnalyticsWrapper from '../../components/AnalyticsWrapper';
import { PopupTrigger } from '../../components/PopupTrigger';
import { TriggerButton } from '../../components/TriggerButton';
import { useIsResponsiveView } from '../../utils';

import { popupContentEntryPoint } from './entrypoint';

function UniversalCreateButtonInner(props: UniversalCreateButtonCombinedProps) {
	const {
		children,
		testId,
		onClick,
		onClose,
		enableTriggerPulse = false,
		triggerButtonSpotlightTarget,
		isNav4Enabled = true,
		buttonTheme,
		onMouseEnter,
		onMouseLeave,
		shouldRenderToParent,
	} = props;

	const isResponsive = useIsResponsiveView();

	const handleTriggerButtonClick = useCallback(() => {
		onClick?.();
	}, [onClick]);

	const handleClose = useCallback(() => {
		onClose?.();
	}, [onClose]);

	const handleMouseEnter = useCallback(
		(event: React.MouseEvent<HTMLButtonElement, MouseEvent>) => {
			onMouseEnter?.(event);
		},
		[onMouseEnter],
	);

	return (
		<PopupTrigger
			placement={isResponsive && isNav4Enabled ? 'bottom-end' : 'bottom-start'}
			// eslint-disable-next-line @atlaskit/design-system/use-should-render-to-parent
			shouldRenderToParent={shouldRenderToParent}
			onClose={handleClose}
			trigger={(triggerProps) => (
				<TriggerButton
					triggerProps={triggerProps}
					testId={testId}
					triggerButtonSpotlightTarget={triggerButtonSpotlightTarget}
					enableTriggerPulse={enableTriggerPulse}
					isOpen={triggerProps['aria-expanded']}
					onMouseEnter={handleMouseEnter}
					onMouseLeave={onMouseLeave}
					onClick={handleTriggerButtonClick}
					isNav4Enabled={isNav4Enabled}
					buttonTheme={buttonTheme}
				>
					{children}
				</TriggerButton>
			)}
			entryPoint={popupContentEntryPoint}
			entryPointProps={props}
		/>
	);
}

export function UniversalCreateButtonLazy(props: UniversalCreateButtonCombinedProps) {
	return (
		<ConditionalRelayEnvironmentProvider>
			<BFFProvider consumer={BFFConsumerEnum.UNIVERSAL_CREATE}>
				<AnalyticsWrapper>
					<FlagsProvider>
						<UniversalCreateButtonInner {...props} />
					</FlagsProvider>
				</AnalyticsWrapper>
			</BFFProvider>
		</ConditionalRelayEnvironmentProvider>
	);
}
